import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Menu, User, LogOut, ChevronDown, ExternalLink } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { RootState } from '../../store';
import { logout } from '../../store/slices/authSlice';
import { useGetDealerProfileQuery } from '../../store/slices/apiSlice';
import toast from 'react-hot-toast';

interface DealerHeaderProps {
  onMenuClick: () => void;
}

const DealerHeader: React.FC<DealerHeaderProps> = ({ onMenuClick }) => {
  const { user } = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch();
  const { data: profile, isLoading } = useGetDealerProfileQuery();
  const navigate = useNavigate();
  const location = useLocation();

  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        buttonRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setUserMenuOpen(false);
      }
    };

    if (userMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [userMenuOpen]);

  const handleLogout = () => {
    dispatch(logout());
    toast.success('Logged out successfully!');
    navigate('/');
    setUserMenuOpen(false);
  };

  // Get profile image with fallback
  const getProfileImage = () => {
    if (isLoading) {
      return null; // Return null during loading to show icon
    }
    if (profile?.data?.profile_image) {
      return profile.data.profile_image;
    }
    return null; // Return null instead of default image to show icon
  };

  // Get user initial for fallback
  const getUserInitial = () => {
    return profile?.data?.name?.charAt(0)?.toUpperCase() || user?.name?.charAt(0)?.toUpperCase() || 'D';
  };

  // Check if we should show profile icon
  const shouldShowIcon = () => {
    return isLoading || !profile?.data?.profile_image;
  };

  const getPageTitle = () => {
    const path = location.pathname;
    if (path === '/dealer' || path === '/dealer/' || path === '/dealer/dashboard') return 'Dealer Dashboard';
    if (path.startsWith('/dealer/profile')) return 'Dealer Profile';
    if (path.startsWith('/dealer/wallet')) return 'Dealer Wallet';
    if (path.startsWith('/dealer/listings/create')) return 'Create Listing';
    if (path.startsWith('/dealer/listings/sold')) return 'Sold Listings';
    if (path.includes('/edit')) return 'Edit Listing';
    if (path.startsWith('/dealer/listings/')) return 'View Listing';
    if (path.startsWith('/dealer/listings')) return 'My Listings';
    return 'Dealer Panel';
  };

  return (
    <header className="bg-white/80 backdrop-blur-md border-b border-gray-200/50 sticky top-0 z-30">
      <div className="px-4 md:px-6 lg:px-8 py-4">
        <div className="flex items-center justify-between">
          {/* Left Section */}
          <div className="flex items-center gap-4">
            <button
              className="xl:hidden p-2 hover:bg-gray-100 rounded-xl transition-all duration-200 group"
              onClick={onMenuClick}
            >
              <Menu className="h-5 w-5 text-gray-600 group-hover:text-gray-900" />
            </button>
            <h1 className="text-lg font-bold text-gray-800 md:text-xl">
              {getPageTitle()}
            </h1>
          </div>

          {/* Right Section */}
          <div className="flex items-center gap-3 md:gap-5">
            {/* Go to App Button */}
            <a
              href="https://app.diasporawise.com"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:flex lg:hidden items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-xl shadow-lg shadow-blue-200 transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98]"
            >
              <ExternalLink className="w-4 h-4" />
              <span>Go to App</span>
            </a>

            {/* Mobile icon version */}
            <a
              href="https://app.diasporawise.com"
              target="_blank"
              rel="noopener noreferrer"
              className="sm:hidden p-2 bg-blue-600 text-white rounded-xl shadow-lg shadow-blue-200"
              title="Go to App"
            >
              <ExternalLink className="w-5 h-5" />
            </a>

            <div className="h-8 w-px bg-gray-200 mx-1 hidden sm:block"></div>

            {/* User Profile */}
            <div className="relative">
              <button
                ref={buttonRef}
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-2 sm:gap-3 p-2 hover:bg-gray-100 rounded-xl transition-all duration-200"
              >
                <div className="relative w-8 h-8 rounded-full overflow-hidden">
                  {getProfileImage() ? (
                    <>
                      <img
                        src={getProfileImage()}
                        alt={profile?.data?.name || user?.name || 'Dealer'}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          // Fallback to initial if image fails to load
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          const fallback = target.nextElementSibling as HTMLElement;
                          if (fallback) fallback.style.display = 'flex';
                        }}
                      />
                      <div
                        className="w-full h-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-semibold text-sm"
                        style={{ display: 'none' }}
                      >
                        {getUserInitial()}
                      </div>
                    </>
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white">
                      <User className="w-4 h-4" />
                    </div>
                  )}
                </div>
                <div className="hidden sm:block text-left">
                  <p className="text-sm font-medium text-gray-900">
                    {profile?.data?.name || user?.name || 'Dealer User'}
                  </p>
                  <p className="text-xs text-gray-500 capitalize">
                    Dealer
                  </p>
                </div>
                <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform duration-200 ${userMenuOpen ? 'rotate-180' : ''
                  }`} />
              </button>

              {/* User Menu Dropdown */}
              {userMenuOpen && (
                <div
                  ref={dropdownRef}
                  onMouseLeave={() => setUserMenuOpen(false)}
                  className="absolute right-0 top-full mt-2 w-64 bg-white rounded-xl shadow-xl border border-gray-200 z-50"
                >
                  <div className="p-4 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                      <div className="relative w-12 h-12 rounded-full overflow-hidden">
                        {getProfileImage() ? (
                          <>
                            <img
                              src={getProfileImage()}
                              alt={profile?.data?.name || user?.name || 'Dealer'}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                // Fallback to initial if image fails to load
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                                const fallback = target.nextElementSibling as HTMLElement;
                                if (fallback) fallback.style.display = 'flex';
                              }}
                            />
                            <div
                              className="w-full h-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-semibold"
                              style={{ display: 'none' }}
                            >
                              {getUserInitial()}
                            </div>
                          </>
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white">
                            <User className="w-6 h-6" />
                          </div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-gray-900 truncate">
                          {profile?.data?.name || user?.name || 'Dealer User'}
                        </p>
                        <p className="text-sm text-gray-500 truncate">
                          {profile?.data?.email || user?.email}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="p-2">
                    <button
                      onClick={() => {
                        navigate('/dealer/profile');
                        setUserMenuOpen(false);
                      }}
                      className="w-full flex items-center gap-3 px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-all duration-200 text-sm"
                    >
                      <User className="w-4 h-4" />
                      <span>Profile</span>
                    </button>
                  </div>

                  <div className="border-t border-gray-100 p-2">
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-3 px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200 text-sm"
                    >
                      <LogOut className="w-4 h-4" />
                      <span>Sign Out</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default DealerHeader;

